import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

// Types aligned with the existing schema
export interface ImageFolder {
  id: string;
  name: string;
  description: string | null;
  parent_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface RepositoryImage {
  id: string;
  folder_id: string | null;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  description: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Added for convenience
  public_url?: string;
}

interface UseRepositoryImagesProps {
  searchQuery?: string;
  folderId?: string | null;
  page?: number;
  pageSize?: number;
}

// Main hook for fetching repository images
export const useRepositoryImages = ({
  searchQuery = '',
  folderId,
  page = 1,
  pageSize = 20,
}: UseRepositoryImagesProps = {}) => {
  return useQuery({
    queryKey: ['repository-images', { searchQuery, folderId, page, pageSize }],
    queryFn: async () => {
      // Calculate pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // Build query
      let query = supabase
        .from('images')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);

      // Apply filters
      if (folderId) {
        query = query.eq('folder_id', folderId);
      }

      if (searchQuery) {
        query = query.or(
          `file_name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`
        );
      }

      const { data, error, count } = await query;

      if (error) throw error;

      // Transform URLs to public URLs
      const transformedImages: RepositoryImage[] = (data || []).map(
        (image) => ({
          ...image,
          public_url: supabase.storage
            .from('image_repository')
            .getPublicUrl(image.file_path).data.publicUrl,
        })
      );

      return {
        images: transformedImages,
        totalCount: count || 0,
        hasMore: (count || 0) > page * pageSize,
        currentPage: page,
        totalPages: Math.ceil((count || 0) / pageSize),
      };
    },
    staleTime: 30000,
  });
};

// Hook for fetching folders
export const useImageFolders = () => {
  return useQuery({
    queryKey: ['image-folders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('image_folders')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || [];
    },
    staleTime: 60000,
  });
};

// Hook for creating folders
export const useCreateFolder = () => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: async (folder: {
      name: string;
      description?: string;
      parent_id?: string | null;
    }) => {
      const { data, error } = await supabase
        .from('image_folders')
        .insert({
          name: folder.name,
          description: folder.description || null,
          parent_id: folder.parent_id || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['image-folders'] });
      success('Success', 'Folder created successfully');
    },
    onError: (err) => {
      error('Error', 'Failed to create folder');
      console.error(err);
    },
  });
};

// Hook for uploading images
export const useUploadImage = () => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: async ({
      file,
      folderId,
      description,
    }: {
      file: File;
      folderId?: string | null;
      description?: string;
    }) => {
      // Generate file path
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random()
        .toString(36)
        .substring(2)}.${fileExt}`;
      const filePath = folderId ? `${folderId}/${fileName}` : fileName;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('image_repository')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Create database record
      const { data, error } = await supabase
        .from('images')
        .insert({
          folder_id: folderId || null,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type,
          description: description || null,
          metadata: {
            original_name: file.name,
            uploaded_from: 'admin-dashboard',
            upload_timestamp: new Date().toISOString(),
          },
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['repository-images'] });
      success('Success', 'Image uploaded successfully');
    },
    onError: (err) => {
      error('Error', 'Failed to upload image');
      console.error(err);
    },
  });
};

// Hook for uploading multiple images
export const useUploadMultipleImages = () => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: async ({
      files,
      folderId,
      description,
    }: {
      files: File[];
      folderId?: string | null;
      description?: string;
    }) => {
      const results = [];

      for (const file of files) {
        try {
          // Generate file path
          const fileExt = file.name.split('.').pop();
          const fileName = `${Date.now()}-${Math.random()
            .toString(36)
            .substring(2)}.${fileExt}`;
          const filePath = folderId ? `${folderId}/${fileName}` : fileName;

          // Upload to storage
          const { error: uploadError } = await supabase.storage
            .from('image_repository')
            .upload(filePath, file);

          if (uploadError) throw uploadError;

          // Create database record
          const { data, error } = await supabase
            .from('images')
            .insert({
              folder_id: folderId || null,
              file_name: file.name,
              file_path: filePath,
              file_size: file.size,
              mime_type: file.type,
              description: description || null,
              metadata: {
                original_name: file.name,
                uploaded_from: 'admin-dashboard',
                upload_timestamp: new Date().toISOString(),
              },
            })
            .select()
            .single();

          if (error) throw error;
          results.push(data);
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          // Continue with other files
        }
      }

      return results;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['repository-images'] });
      success('Success', `Uploaded ${data.length} images successfully`);
    },
    onError: (err) => {
      error('Error', 'Failed to upload images');
      console.error(err);
    },
  });
};

// Hook for updating image details
export const useUpdateImage = () => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      description,
      metadata,
    }: {
      id: string;
      description?: string;
      metadata?: Record<string, unknown>;
    }) => {
      const updateData: {
        description?: string;
        metadata?: Record<string, unknown>;
      } = {};
      if (description !== undefined) updateData.description = description;
      if (metadata !== undefined) updateData.metadata = metadata;

      const { data, error } = await supabase
        .from('images')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['repository-images'] });
      success('Success', 'Image updated successfully');
    },
    onError: (err) => {
      error('Error', 'Failed to update image');
      console.error(err);
    },
  });
};

// Hook for deleting images
export const useDeleteImage = () => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: async (image: RepositoryImage) => {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('image_repository')
        .remove([image.file_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error } = await supabase
        .from('images')
        .delete()
        .eq('id', image.id);

      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['repository-images'] });
      success('Success', 'Image deleted successfully');
    },
    onError: (err) => {
      error('Error', 'Failed to delete image');
      console.error(err);
    },
  });
};

// Hook for moving images between folders
export const useMoveImage = () => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: async ({
      imageId,
      targetFolderId,
    }: {
      imageId: string;
      targetFolderId: string | null;
    }) => {
      const { data, error } = await supabase
        .from('images')
        .update({ folder_id: targetFolderId })
        .eq('id', imageId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['repository-images'] });
      success('Success', 'Image moved successfully');
    },
    onError: (err) => {
      error('Error', 'Failed to move image');
      console.error(err);
    },
  });
};

// Hook for deleting folders
export const useDeleteFolder = () => {
  const queryClient = useQueryClient();
  const { error: toastError, success } = useToast();

  return useMutation({
    mutationFn: async (folderId: string) => {
      // Check if folder has images
      const { count } = await supabase
        .from('images')
        .select('id', { count: 'exact' })
        .eq('folder_id', folderId)
        .limit(0);

      if (count && count > 0) {
        throw new Error(
          'Cannot delete folder with images. Please move or delete images first.'
        );
      }

      // Check if folder has subfolders
      const { count: subfolderCount } = await supabase
        .from('image_folders')
        .select('id', { count: 'exact' })
        .eq('parent_id', folderId)
        .limit(0);

      if (subfolderCount && subfolderCount > 0) {
        throw new Error(
          'Cannot delete folder with subfolders. Please delete subfolders first.'
        );
      }

      // Delete folder
      const { error } = await supabase
        .from('image_folders')
        .delete()
        .eq('id', folderId);

      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['image-folders'] });
      success('Success', 'Folder deleted successfully');
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : 'Failed to delete folder';
      toastError('Error', message);
      console.error(err);
    },
  });
};

// Helper function to build folder tree
export const buildFolderTree = (folders: ImageFolder[]): ImageFolder[] => {
  const folderMap = new Map<
    string,
    ImageFolder & { children?: ImageFolder[] }
  >();
  const rootFolders: (ImageFolder & { children?: ImageFolder[] })[] = [];

  // First pass: create map
  folders.forEach((folder) => {
    folderMap.set(folder.id, { ...folder, children: [] });
  });

  // Second pass: build tree
  folders.forEach((folder) => {
    const folderWithChildren = folderMap.get(folder.id)!;
    if (folder.parent_id) {
      const parent = folderMap.get(folder.parent_id);
      if (parent) {
        parent.children!.push(folderWithChildren);
      } else {
        rootFolders.push(folderWithChildren);
      }
    } else {
      rootFolders.push(folderWithChildren);
    }
  });

  return rootFolders;
};
